// SPDX-FileCopyrightText: Copyright 2024 Dash0 Inc.

import { ReactNode } from "react";

import {
  defaultNoDataIcon,
  NoDataMessage,
} from "@/components/ui/no-data-message";
import { IntegrationCard } from "@/hub/components/integration-card";
import { SectionHeading } from "@/hub/components/section-heading";
import { ExtendedIntegrationMetadata } from "@/hub/types";
import { compareIntegrationsWithinCategory } from "@/hub/utils/compare-integrations-within-category";
import { emptyFrozenArray } from "@/lib/utils/array";
import clsx from "clsx";

interface IntegrationsListProps {
  heading?: ReactNode;
  allIntegrations: ExtendedIntegrationMetadata[];
  searchTerm?: string;
  isRootLevel?: boolean;
  wrapperClassName?: string;
  useSpanForTitles?: boolean;
}

export function IntegrationsList({
  heading,
  allIntegrations,
  searchTerm,
  isRootLevel,
  wrapperClassName,
  useSpanForTitles
}: IntegrationsListProps) {

  let integrationsToShow = allIntegrations;
  if (isRootLevel) {
    integrationsToShow = allIntegrations.filter(
      (i) => i.indexEntry.parent == null || i.recommended
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {heading != null && <SectionHeading>{heading}</SectionHeading>}
      {integrationsToShow.length > 0 ? (
        <div className={clsx("grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-3",
          wrapperClassName
        )}>
          {integrationsToShow
            .slice()
            .sort(compareIntegrationsWithinCategory)
            .map((integration, i) => {
              let subIntegrations: ExtendedIntegrationMetadata[] = allIntegrations.filter(
                (i) => i.indexEntry.parent?.id === integration.id
              );

              return (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  subIntegrations={subIntegrations}
                  prefetch={i < 10}
                  forceOpen={searchTerm != null && searchTerm !== ""}
                  useSpanForTitles={useSpanForTitles}
                />
              );
            })}
        </div>
      ) : (
        <NoDataMessage
          icon={defaultNoDataIcon}
          message={
            <>{`No integrations matching your search term ${(
              <code>{searchTerm}</code>
            )} found.`}</>
          }
          className="mt-1"
        />
      )}
    </div>
  );
}
